import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Link2, Loader2, ImageIcon } from "lucide-react";
import { endpoints, API_BASE } from "@/lib/api";

// Absolute URL so uploaded images render on both admin and public sides in preview & prod.
const toAbsolute = (u) => (u && u.startsWith("/api/") ? `${API_BASE}${u}` : u);

export function ImageUpload({ value, onChange, label = "Image", testid = "image", aspect = "aspect-video" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await endpoints.uploadImage(fd);
      onChange(toAbsolute(data.url));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      {label && <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</span>}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testid}-file-input`} />
      <div className="mt-1.5">
        {value ? (
          <div className={`relative ${aspect} w-full rounded-xl overflow-hidden border border-[#273042] bg-[#0A0C10] group`}>
            <img src={value} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button type="button" onClick={pick} disabled={busy} data-testid={`${testid}-replace-btn`}
                className="h-9 px-3 rounded-lg bg-white/15 text-white text-sm font-semibold flex items-center gap-1.5">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Replace
              </button>
              <button type="button" onClick={() => onChange("")} data-testid={`${testid}-remove-btn`}
                className="h-9 px-3 rounded-lg bg-red-500/80 text-white text-sm font-semibold flex items-center gap-1.5">
                <X className="w-4 h-4" /> Remove
              </button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={pick} disabled={busy} data-testid={`${testid}-upload-btn`}
            className={`${aspect} w-full rounded-xl border-2 border-dashed border-[#273042] bg-[#0A0C10] hover:border-[#E11D2A]/60 transition-colors flex flex-col items-center justify-center gap-2 text-slate-400`}>
            {busy ? <Loader2 className="w-6 h-6 animate-spin text-[#E11D2A]" /> : <ImageIcon className="w-7 h-7" />}
            <span className="text-sm font-medium">{busy ? "Uploading…" : "Upload from computer / phone"}</span>
            <span className="text-xs text-slate-500">JPG, PNG, WEBP, GIF · up to 12 MB</span>
          </button>
        )}
      </div>
      <button type="button" onClick={() => setShowUrl((s) => !s)} data-testid={`${testid}-url-toggle`}
        className="mt-2 text-xs text-slate-400 hover:text-white inline-flex items-center gap-1.5">
        <Link2 className="w-3.5 h-3.5" /> {showUrl ? "Hide URL option" : "Or paste an image URL"}
      </button>
      {showUrl && (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://…"
          data-testid={`${testid}-url-input`}
          className="mt-1.5 w-full h-10 rounded-lg bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm outline-none focus:border-[#E11D2A]/60" />
      )}
    </div>
  );
}
